import React, { useState } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import { useCrmData } from '../../contexts/CrmProviders';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useChatContext } from '../../contexts/useChatContext';
import { isOrgAdminRole } from '../../domain/support/guards';
import { cn } from '@/lib/utils';
import { SidebarAccountSection } from './sidebar/SidebarAccountSection';
import { SidebarLogo } from './sidebar/SidebarLogo';
import { SidebarNavigation } from './sidebar/SidebarNavigation';
import { MobileBottomNav } from './sidebar/MobileBottomNav';
import { useHasPartnerRecord } from '../../features/partner/hooks/useHasPartnerRecord';

interface SidebarProps {
    isBlocked?: boolean;
    isOpen?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isBlocked = false, isOpen = false }) => {
    const { currentPage, setCurrentPage } = useAppContext();
    const { activeConversationId } = useChatContext();
    const { clientProfile } = useCrmData();
    const { user, logout } = useAuth();
    const { canViewBilling } = usePermissions();
    const { currentOrganization } = useOrganization();
    const canViewSupport = isOrgAdminRole(currentOrganization?.role);
    const hasPartnerAccess = useHasPartnerRecord();
    const [avatarError, setAvatarError] = useState(false);
    const [avatarLoaded, setAvatarLoaded] = useState(false);

    // Chat: menu só na lista; conversa / painel de contato = imersivo (sem bottom nav).
    const hideMobileNav = currentPage === 'chat' && Boolean(activeConversationId);

    const displayName = clientProfile?.name || user?.user_metadata?.full_name || 'Usuário';
    const initials = (clientProfile?.name || user?.user_metadata?.full_name || 'U').substring(0, 2).toUpperCase();

    const accountProps = {
        isOpen,
        isBlocked,
        currentPage,
        setCurrentPage,
        logout,
        canViewBilling,
        canViewSupport,
        hasPartnerAccess,
        clientProfileName: clientProfile?.name,
        displayName,
        userEmail: user?.email,
        userFullName: user?.user_metadata?.full_name,
        initials,
        avatarUrl: user?.user_metadata?.avatar_url,
        avatarError,
        setAvatarError,
        avatarLoaded,
        setAvatarLoaded,
    };

    return (
        <aside
            className={cn(
                'w-full shrink-0 bg-card transition-all duration-300',
                'fixed bottom-0 left-0 z-50 border-t border-subtle',
                'max-md:pb-[max(0.5rem,env(safe-area-inset-bottom))] max-md:pt-1',
                'md:relative md:z-30 md:flex md:h-screen md:flex-col md:border-t-0 md:border-r md:border-subtle md:px-0 md:pb-3 md:pt-4',
                isOpen ? 'md:w-60 md:items-start' : 'md:w-16 md:items-center',
                hideMobileNav ? 'hidden md:flex' : 'flex max-md:flex-col'
            )}
        >
            <SidebarLogo isOpen={isOpen} />

            {!isBlocked ? (
                <>
                    <MobileBottomNav
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        accountSlot={<SidebarAccountSection {...accountProps} />}
                    />
                    <SidebarNavigation isOpen={isOpen} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                    <div className="hidden md:mt-auto md:block md:w-full">
                        <SidebarAccountSection {...accountProps} />
                    </div>
                </>
            ) : (
                <div className="w-full max-md:px-2">
                    <SidebarAccountSection {...accountProps} />
                </div>
            )}
        </aside>
    );
};

export default Sidebar;
